import { merge } from '../../helper';

import type { IOptionConfig, TypeOptionDefaults, TypeOptionItem, TypeOptionKey } from './types';

import * as exElement from '../../utils/element';

import isPlainObject from '../../general/isPlainObject';

// ----------------------------------------------------------------------------

function data(config: IOptionConfig): HTMLOptionElement[] {
    // ------------------------------------------------------------------------

    // Default Config
    const defConfig: IOptionConfig = {
        value: 'value',
        text: 'text',
        disabled: 'disabled',
    };

    // Merge
    merge(defConfig, config);

    // ------------------------------------------------------------------------

    //
    const list: HTMLOptionElement[] = [];
    const optionsConfig = defConfig as TypeOptionDefaults & IOptionConfig;

    // ------------------------------------------------------------------------

    const fnCreateOption = function (value: string, text: string, disabled: boolean): HTMLOptionElement {
        const elOption = exElement.create({
            tag: 'option',
            attr: {
                value: value,
            },
        }) as HTMLOptionElement;

        elOption.textContent = text;
        elOption.disabled = disabled;

        return elOption;
    };

    function fnPushOption(value: unknown, text: unknown, disabled = false): void {
        list.push(fnCreateOption(String(value), String(text), disabled));
    }

    function fnResolveValueOrFunc(
        item: Record<string, unknown>,
        valOrFunc: IOptionConfig[TypeOptionKey],
        index?: number,
    ): unknown {
        if (valOrFunc === null || valOrFunc === undefined) {
            return undefined;
        }

        if (typeof valOrFunc === 'function') {
            return valOrFunc(item, index);
        }

        if (item !== null && item !== undefined && valOrFunc in item) {
            return item[valOrFunc];
        }

        return undefined;
    }

    function fnIsPlainObjectItem(item: TypeOptionItem): item is Record<string, unknown> {
        return isPlainObject(item);
    }

    function fnProcessObjectItem(item: Record<string, unknown>, index?: number): void {
        const resolvedValue = fnResolveValueOrFunc(item, optionsConfig.value, index);
        const resolvedText = fnResolveValueOrFunc(item, optionsConfig.text, index);
        const resolvedDisabled = fnResolveValueOrFunc(item, optionsConfig.disabled, index);

        if (resolvedValue !== undefined && resolvedText !== undefined) {
            fnPushOption(resolvedValue, resolvedText, Boolean(resolvedDisabled));
            return;
        }

        const keys = Object.keys(item);

        if (keys.length === 1) {
            const key = keys[0];

            fnPushOption(key, item[key]);
            return;
        }

        for (const key of keys) {
            fnPushOption(key, item[key]);
        }
    }

    function fnProcessItem(item: TypeOptionItem, index?: number): void {
        if (typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean') {
            fnPushOption(item, item);
            return;
        }

        if (fnIsPlainObjectItem(item)) {
            fnProcessObjectItem(item, index);
            return;
        }

        fnPushOption(item, item);
    }

    // ------------------------------------------------------------------------

    //
    if (Array.isArray(optionsConfig.data)) {
        for (let i = 0; i < optionsConfig.data.length; i++) {
            fnProcessItem(optionsConfig.data[i], i);
        }
    }
    //
    else if (typeof optionsConfig.data === 'object' && optionsConfig.data !== null) {
        fnProcessItem(optionsConfig.data);
    }

    // ------------------------------------------------------------------------

    //
    return list;
}

// ----------------------------------------------------------------------------

export default data;
